import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, TextareaControl, Button, ButtonGroup, ToggleControl, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __, sprintf } from "@wordpress/i18n";
import { isDuplicateTocAnchor } from "../../../shared/js/tocAnchorDiagnostics";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Inline", "content-egg"), value: "inline" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

const VERDICT_TONE_OPTIONS = [
    { label: __("Negative", "content-egg"), value: "false" },
    { label: __("Warning", "content-egg"), value: "partial" },
    { label: __("Positive", "content-egg"), value: "true" },
    { label: __("Neutral", "content-egg"), value: "neutral" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, section_label, title, heading_tag, label_myth, label_fact, label_why, items = [], anchor, toc_label, include_in_toc, level, color_scheme } = attributes;
    const allBlocks = useSelect((select) => select("core/block-editor").getBlocks(), []);
    const hasDuplicateTocAnchor = !!include_in_toc && isDuplicateTocAnchor(allBlocks, anchor);
    const suggestedTocLabel = (title || section_label || "").trim();

    const updateItems = (nextItems) => setAttributes({ items: nextItems });
    const addItem = () => updateItems([...(items || []), { myth_text: "", fact_text: "", why_text: "", verdict: "", verdict_tone: "false", verdict_text: "" }]);
    const updateItem = (index, key, value) => {
        const next = [...(items || [])];
        next[index] = {
            myth_text: "",
            fact_text: "",
            why_text: "",
            verdict: "",
            verdict_tone: "false",
            verdict_text: "",
            ...(next[index] || {}),
            [key]: value,
        };
        updateItems(next);
    };
    const removeItem = (index) => {
        const next = [...(items || [])];
        next.splice(index, 1);
        updateItems(next);
    };

    const hasPreviewContent = (items || []).some((item) => {
        const myth = (item?.myth_text || "").trim();
        if (variant === "inline") {
            return myth !== "" && (item?.verdict || "").trim() !== "";
        }
        return myth !== "" && (item?.fact_text || "").trim() !== "";
    });

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Section Label", "content-egg")}
                        value={section_label}
                        onChange={(value) => setAttributes({ section_label: value })}
                    />
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {["h1", "h2", "h3", "h4", "div"].map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                </PanelBody>

                {variant === "default" && (
                    <PanelBody title={__("Labels", "content-egg")} initialOpen={false}>
                        <TextControl
                            label={__("Myth Label", "content-egg")}
                            value={label_myth}
                            onChange={(value) => setAttributes({ label_myth: value })}
                        />
                        <TextControl
                            label={__("Fact Label", "content-egg")}
                            value={label_fact}
                            onChange={(value) => setAttributes({ label_fact: value })}
                        />
                        <TextControl
                            label={__("Why Label", "content-egg")}
                            value={label_why}
                            onChange={(value) => setAttributes({ label_why: value })}
                            __nextHasNoMarginBottom
                        />
                    </PanelBody>
                )}

                <PanelBody title={__("Items", "content-egg")} initialOpen={false}>
                    {(items || []).map((item, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: index === items.length - 1 ? "0" : "10px",
                                paddingBottom: index === items.length - 1 ? "0" : "10px",
                                borderBottom: index === items.length - 1 ? "none" : "1px solid #e0e0e0",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                                <Button
                                    icon="no-alt"
                                    isDestructive
                                    size="small"
                                    onClick={() => removeItem(index)}
                                    label={__("Remove", "content-egg")}
                                />
                            </div>

                            <TextControl
                                label={__("Myth", "content-egg")}
                                value={item?.myth_text || ""}
                                onChange={(value) => updateItem(index, "myth_text", value)}
                            />

                            {variant === "default" ? (
                                <>
                                    <TextareaControl
                                        label={__("Fact", "content-egg")}
                                        value={item?.fact_text || ""}
                                        onChange={(value) => updateItem(index, "fact_text", value)}
                                        rows={3}
                                    />
                                    <TextareaControl
                                        label={__("Why It Matters", "content-egg")}
                                        value={item?.why_text || ""}
                                        onChange={(value) => updateItem(index, "why_text", value)}
                                        rows={3}
                                        __nextHasNoMarginBottom
                                    />
                                </>
                            ) : (
                                <>
                                    <TextControl
                                        label={__("Verdict", "content-egg")}
                                        value={item?.verdict || ""}
                                        onChange={(value) => updateItem(index, "verdict", value)}
                                    />
                                    <SelectControl
                                        label={__("Verdict Tone", "content-egg")}
                                        value={item?.verdict_tone || "false"}
                                        options={VERDICT_TONE_OPTIONS}
                                        onChange={(value) => updateItem(index, "verdict_tone", value)}
                                    />
                                    <TextareaControl
                                        label={__("Verdict Text", "content-egg")}
                                        value={item?.verdict_text || ""}
                                        onChange={(value) => updateItem(index, "verdict_text", value)}
                                        rows={3}
                                        __nextHasNoMarginBottom
                                    />
                                </>
                            )}
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addItem} style={{ marginTop: "10px" }}>
                        {__("Add item", "content-egg")}
                    </Button>
                </PanelBody>

                <PanelBody title={__("TOC", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Anchor", "content-egg")}
                        value={anchor || ""}
                        onChange={(value) => setAttributes({ anchor: value })}
                    />
                    {hasDuplicateTocAnchor && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#8a1f11", fontSize: "12px" }}>
                            {__("This anchor is already used by another TOC block.", "content-egg")}
                        </p>
                    )}
                    <ToggleControl
                        label={__("Include In TOC", "content-egg")}
                        checked={!!include_in_toc}
                        onChange={(value) => setAttributes({ include_in_toc: value })}
                    />
                    {include_in_toc && !(anchor || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {__("Set an anchor so the TOC can link to this block.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("TOC Label", "content-egg")}
                        value={toc_label || ""}
                        onChange={(value) => setAttributes({ toc_label: value })}
                    />
                    {include_in_toc && !(toc_label || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {suggestedTocLabel
                                ? sprintf(__("Suggested label: %s", "content-egg"), `"${suggestedTocLabel}"`)
                                : __("Set a TOC label so this block can appear in automatic TOC mode.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("Level", "content-egg")}
                        type="number"
                        min={1}
                        max={6}
                        value={level ?? 2}
                        onChange={(value) => setAttributes({ level: value === "" ? 2 : Number(value) })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/myth-fact"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add myth/fact items in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
